import { Card } from "@/components/ui/card";
import { MessageSquare, Clock, Users, TrendingUp, ThumbsUp, ThumbsDown } from "lucide-react";
import type { AnalyticsData } from "@/hooks/useAnalytics";

interface AnalyticsSummaryCardsProps {
  analytics: AnalyticsData;
}

export const AnalyticsSummaryCards = ({ analytics }: AnalyticsSummaryCardsProps) => {
  const formatTime = (minutes: number) => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      <Card className="p-4 bg-gradient-to-br from-card to-muted/20 border-0 shadow-[var(--shadow-soft)]">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">Total Conversations</p>
            <p className="text-2xl font-bold text-primary">{analytics.totalConversations}</p>
          </div>
          <MessageSquare className="h-8 w-8 text-primary/40" />
        </div>
      </Card>

      <Card className="p-4 bg-gradient-to-br from-card to-muted/20 border-0 shadow-[var(--shadow-soft)]">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">Avg Response Time</p>
            <p className="text-2xl font-bold text-accent">{formatTime(analytics.avgResponseTime)}</p>
          </div>
          <Clock className="h-8 w-8 text-accent/40" />
        </div>
      </Card>

      <Card className="p-4 bg-gradient-to-br from-card to-muted/20 border-0 shadow-[var(--shadow-soft)]">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">Avg Resolution Time</p>
            <p className="text-2xl font-bold">{formatTime(analytics.avgResolutionTime)}</p>
          </div>
          <Clock className="h-8 w-8 text-muted-foreground/40" />
        </div>
      </Card>

      <Card className="p-4 bg-gradient-to-br from-card to-muted/20 border-0 shadow-[var(--shadow-soft)]">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">Resolved</p>
            <p className="text-2xl font-bold text-primary">{analytics.resolvedConversations}</p>
          </div>
          <TrendingUp className="h-8 w-8 text-primary/40" />
        </div>
      </Card>

      <Card className="p-4 bg-gradient-to-br from-card to-muted/20 border-0 shadow-[var(--shadow-soft)]">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">New Customers</p>
            <p className="text-2xl font-bold text-accent">{analytics.newCustomers}</p>
          </div>
          <Users className="h-8 w-8 text-accent/40" />
        </div>
      </Card>

      <Card className="p-4 bg-gradient-to-br from-card to-muted/20 border-0 shadow-[var(--shadow-soft)]">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">Returning Customers</p>
            <p className="text-2xl font-bold">{analytics.returningCustomers}</p>
          </div>
          <Users className="h-8 w-8 text-muted-foreground/40" />
        </div>
      </Card>

      <Card className="p-4 bg-gradient-to-br from-card to-muted/20 border-0 shadow-[var(--shadow-soft)]">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">Avg Messages/Conv</p>
            <p className="text-2xl font-bold">{analytics.avgMessagesPerConversation}</p>
          </div>
          <MessageSquare className="h-8 w-8 text-muted-foreground/40" />
        </div>
      </Card>

      <Card className="p-4 bg-gradient-to-br from-card to-muted/20 border-0 shadow-[var(--shadow-soft)]">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">Positive Sentiment</p>
            <p className="text-2xl font-bold text-primary">{analytics.positiveSentiment}</p>
          </div>
          <ThumbsUp className="h-8 w-8 text-primary/40" />
        </div>
      </Card>
    </div>
  );
};
